import { KeyValuePairs, KeyValuePairsProps } from '@amzn/awsui-components-react/polaris';
import { Event, Shift, DetailedShift, ShiftType } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { type TFunction, type i18n } from 'i18next';
import { usePaginatedRedVelvetQuery } from '../../hooks/paginatedRedVelvetQuery';
import { generateLocationInfo, generateEventCoordinatorInfo } from '../shifts/shiftInfo';
import { useTimezone } from '../../hooks/timezone';
import { asDateString } from '../../utils/dateFormatting';

// Event-level display attribute constants
export const DISPLAY_EVENT_LOCATION_INFO = 'eventLocation';
export const DISPLAY_EVENT_DATE_INFO = 'eventDate';
export const DISPLAY_EVENT_COORDINATOR_INFO = 'eventCoordinator';
export const DISPLAY_EVENT_ORGANIZATION_INFO = 'eventOrganization';
export const DISPLAY_SHIFTS_COUNT_INFO = 'shiftsCount';
export const DISPLAY_TOTAL_SIGNUPS_INFO = 'totalSignups';

export type DISPLAY_EVENT_ATTRIBUTES_TYPE =
  | typeof DISPLAY_EVENT_LOCATION_INFO
  | typeof DISPLAY_EVENT_DATE_INFO
  | typeof DISPLAY_EVENT_COORDINATOR_INFO
  | typeof DISPLAY_EVENT_ORGANIZATION_INFO
  | typeof DISPLAY_SHIFTS_COUNT_INFO
  | typeof DISPLAY_TOTAL_SIGNUPS_INFO;

interface UseEventInfoProps {
  event: Event;
  detailedShifts?: DetailedShift[];
  displayAttributes: DISPLAY_EVENT_ATTRIBUTES_TYPE[];
}

function generateOrganizationInfo(event: Event, t: TFunction): KeyValuePairsProps.Item[] | null {
  const communityOrgName = event.communityOrganizations?.[0]?.name;
  if (!communityOrgName) return null;

  return [
    {
      label: t('eventDetails.eventDisplay.organization'),
      value: communityOrgName,
    },
  ];
}

function generateEventDateRangeInfo(
  event: Event,
  shifts: Shift[],
  selectedTimezone: string,
  t: TFunction,
  i18n: i18n
): KeyValuePairsProps.Item[] {
  const filteredShifts = shifts.filter((shift) => shift.shiftType !== ShiftType.WAITLIST);

  const getEarliestStart = (shifts: Shift[]) =>
    shifts.reduce<Date | null>((earliest, shift) => {
      if (!shift.startDateTime) return earliest;
      const shiftStart = new Date(shift.startDateTime);
      return !earliest || shiftStart < earliest ? shiftStart : earliest;
    }, null);

  const getLatestEnd = (shifts: Shift[]) =>
    shifts.reduce<Date | null>((latest, shift) => {
      if (!shift.endDateTime) return latest;
      const shiftEnd = new Date(shift.endDateTime);
      return !latest || shiftEnd > latest ? shiftEnd : latest;
    }, null);

  const formatDateRange = (start: Date, end: Date) => {
    const startStr = asDateString(start, selectedTimezone, i18n.language);
    const endStr = asDateString(end, selectedTimezone, i18n.language);
    return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
  };

  const earliestStart = getEarliestStart(filteredShifts);
  const latestEnd = getLatestEnd(filteredShifts);

  let dateValue: string;
  if (earliestStart && latestEnd) {
    dateValue = formatDateRange(earliestStart, latestEnd);
  } else if (event.startTimestamp && event.endTimestamp) {
    dateValue = formatDateRange(event.startTimestamp, event.endTimestamp);
  } else {
    dateValue = t('eventDisplay.ongoingEvent');
  }

  return [{ label: t('eventDetails.shiftDisplay.date'), value: dateValue }];
}

function generateShiftsCountInfo(shifts: Shift[], t: TFunction): KeyValuePairsProps.Item[] {
  const count = shifts.filter((shift) => shift.shiftType !== ShiftType.WAITLIST).length;
  return [
    {
      label: t('eventDetails.eventDisplay.shiftsCount'),
      value: count,
    },
  ];
}

function generateTotalSignupsInfo(
  detailedShifts: DetailedShift[],
  t: TFunction
): KeyValuePairsProps.Item[] {
  const filteredShifts = detailedShifts.filter(
    (shift) => shift.shift.shiftType !== ShiftType.WAITLIST
  );
  const totalSignups = filteredShifts.reduce((sum, shift) => sum + (shift.signups?.length ?? 0), 0);

  // Check if all shifts have maxSignup defined and greater than 0
  const allShiftsHaveMaxSignup =
    filteredShifts.length > 0 &&
    filteredShifts.every((shift) => shift.shift.maxSignup && shift.shift.maxSignup > 0);

  let value: string | number;
  if (allShiftsHaveMaxSignup) {
    const totalMaxSignup = filteredShifts.reduce(
      (sum, shift) => sum + (shift.shift.maxSignup ?? 0),
      0
    );
    value = t('eventDetails.eventDisplay.signupsFromSpots', {
      signups: totalSignups,
      spots: totalMaxSignup,
    });
  } else {
    value = totalSignups;
  }

  return [
    {
      label: t('eventDetails.eventDisplay.totalSignups'),
      value,
    },
  ];
}

export function useEventInfo({
  event,
  detailedShifts,
  displayAttributes,
}: UseEventInfoProps): KeyValuePairsProps.Item[] {
  const { t, i18n } = useTranslation();
  const selectedTimezone = useTimezone();

  const { queryResult } = usePaginatedRedVelvetQuery({
    enabled: displayAttributes.includes(DISPLAY_EVENT_COORDINATOR_INFO),
    redVelvetQueryKey: [
      'getPeople',
      {
        aliases: event.ownerEmail?.includes('@')
          ? event.ownerEmail.substring(0, event.ownerEmail.indexOf('@'))
          : undefined,
      },
    ],
  });

  const personName =
    queryResult.data && queryResult.data.people && queryResult.data.people.length > 0
      ? `${queryResult.data.people[0].firstName} ${queryResult.data.people[0].lastName}`
      : undefined;

  const shifts: Shift[] = detailedShifts?.map((ds) => ds.shift) ?? [];

  let totalInfo: KeyValuePairsProps.Item[] = [];
  const allAttributes: Record<DISPLAY_EVENT_ATTRIBUTES_TYPE, KeyValuePairsProps.Item[] | null> = {
    eventOrganization: generateOrganizationInfo(event, t),
    eventLocation: generateLocationInfo(event, t),
    shiftsCount: detailedShifts ? generateShiftsCountInfo(shifts, t) : null,
    eventCoordinator: generateEventCoordinatorInfo(event, personName, t),
    eventDate: generateEventDateRangeInfo(event, shifts, selectedTimezone, t, i18n),
    totalSignups: detailedShifts ? generateTotalSignupsInfo(detailedShifts, t) : null,
  };

  // Combine all attributes into the totalInfo array based on displayedAttributes
  displayAttributes.forEach((attribute) => {
    const attributeInfo = allAttributes[attribute];
    if (Array.isArray(attributeInfo)) {
      totalInfo = [...totalInfo, ...attributeInfo];
    }
  });

  return totalInfo;
}

interface EventInfoProps {
  event: Event;
  detailedShifts?: DetailedShift[];
  displayAttributes: DISPLAY_EVENT_ATTRIBUTES_TYPE[];
}

export function EventInfo({ event, detailedShifts, displayAttributes }: EventInfoProps) {
  const info = useEventInfo({ event, detailedShifts, displayAttributes });
  return (
    <div className="event-info">
      <KeyValuePairs columns={3} items={info} />
    </div>
  );
}
